import { create } from 'zustand';
import { onboardingApi, type OnboardingState } from '../services/api/onboarding';

interface OnboardingStore {
  state: OnboardingState;
  loaded: boolean;
  showWizard: boolean;
  showChecklist: boolean;
  isChecklistExpanded: boolean;
  activeTour: string | null;

  load: () => Promise<void>;
  update: (partial: Partial<OnboardingState>) => Promise<void>;
  completeWizard: () => Promise<void>;
  skipWizard: () => Promise<void>;
  completeChecklistItem: (key: string) => Promise<void>;
  completeTour: (tourId: string) => Promise<void>;
  dismissChecklist: () => Promise<void>;
  toggleChecklistExpanded: () => void;
  setShowWizard: (show: boolean) => void;
  resetTour: (tourId: string) => Promise<void>;
  setActiveTour: (tourId: string | null) => void;
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  state: {},
  loaded: false,
  showWizard: false,
  // Default false so there is no FOUC of the checklist pill before load() resolves backend state.
  // After load() the value is set from !state.checklistDismissed (see below).
  showChecklist: false,
  isChecklistExpanded: true,
  activeTour: null,

  load: async () => {
    try {
      const state = await onboardingApi.getState();
      const isNew = !state.wizardCompleted && !state.wizardSkipped;
      set({
        state,
        loaded: true,
        showWizard: isNew,
        showChecklist: !state.checklistDismissed,
        isChecklistExpanded: state.checklistExpanded !== false,
      });
    } catch {
      set({ loaded: true });
    }
  },

  update: async (partial) => {
    try {
      const merged = await onboardingApi.updateState(partial);
      // Preserve auto-detected checklist items from GET (not persisted in DB)
      const currentChecklist = get().state.checklist || {};
      const responseChecklist = merged.checklist || {};
      set({
        state: {
          ...merged,
          checklist: { ...currentChecklist, ...responseChecklist },
        },
      });
    } catch {
      // Silent fail — non-critical
    }
  },

  completeWizard: async () => {
    const now = new Date().toISOString();
    await get().update({ wizardCompleted: true, wizardCompletedAt: now });
    set({ showWizard: false });
  },

  skipWizard: async () => {
    await get().update({ wizardSkipped: true });
    set({ showWizard: false });
  },

  completeChecklistItem: async (key: string) => {
    const current = get().state.checklist || {};
    await get().update({ checklist: { ...current, [key]: true } });
  },

  completeTour: async (tourId: string) => {
    const tours = get().state.tours || {};
    await get().update({
      tours: { ...tours, [tourId]: { completed: true, completedAt: new Date().toISOString() } },
    });
  },

  dismissChecklist: async () => {
    await get().update({ checklistDismissed: true });
    set({ showChecklist: false });
  },

  toggleChecklistExpanded: () => {
    const next = !get().isChecklistExpanded;
    set({ isChecklistExpanded: next });
    // Persist expanded state (fire-and-forget)
    get()
      .update({ checklistExpanded: next } as any)
      .catch(() => {});
  },

  setShowWizard: (show) => set({ showWizard: show }),

  setActiveTour: (tourId) => set({ activeTour: tourId }),

  resetTour: async (tourId: string) => {
    const tours = get().state.tours || {};
    await get().update({
      tours: { ...tours, [tourId]: { completed: false, completedAt: null } },
    });
  },
}));
